import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { StatusBadge, EmergencyBanner, Money } from "@/components/shared/Common";
import { toast } from "sonner";
import { Sparkles, CalendarDays, FolderHeart, MessagesSquare, ArrowRight, Gift, Copy, Check, Trophy } from "lucide-react";

export default function PatientDashboard() {
  const { user } = useAuth();
  const [appts, setAppts] = useState([]);
  const [credits, setCredits] = useState(null);
  const [referral, setReferral] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/appointments/mine").then(r => setAppts(r.data));
    api.get("/patient/credits").then(r => setCredits(r.data)).catch(()=>{});
    api.get("/patient/referral").then(r => setReferral(r.data)).catch(()=>{});
    api.get("/referrals/leaderboard").then(r => setLeaderboard(r.data.leaderboard || [])).catch(()=>{});
  }, []);

  const upcoming = appts.filter(a => a.status !== "completed").slice(0,3);
  const shareUrl = referral ? `${window.location.origin}/register?ref=${referral.referral_code}` : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Referral link copied");
      setTimeout(()=>setCopied(false), 2000);
    } catch { toast.error("Copy failed"); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="cm-eyebrow mb-2">Welcome</div>
        <h1 className="cm-h2">Hello, {user?.first_name || "there"}.</h1>
        <p className="cm-body mt-1 text-sm">Your care, on your terms. Start with our AI health navigator or browse doctors directly.</p>
      </div>
      <EmergencyBanner compact/>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Link to="/patient/intake" className="cm-card group" data-testid="p-quick-intake">
          <div className="w-10 h-10 rounded-lg bg-[#ebf3fc] text-[#4a90d9] flex items-center justify-center mb-3"><Sparkles size={18}/></div>
          <div className="font-semibold text-[#1a2b4a]">Find the right care</div>
          <div className="text-xs text-[#475569] mt-1">Start an AI-assisted intake</div>
        </Link>
        <Link to="/doctors" className="cm-card group" data-testid="p-quick-doctors">
          <div className="w-10 h-10 rounded-lg bg-[#ebf3fc] text-[#4a90d9] flex items-center justify-center mb-3"><CalendarDays size={18}/></div>
          <div className="font-semibold text-[#1a2b4a]">Browse doctors</div>
          <div className="text-xs text-[#475569] mt-1">Book a specific clinician</div>
        </Link>
        <Link to="/patient/care-passport" className="cm-card group" data-testid="p-quick-passport">
          <div className="w-10 h-10 rounded-lg bg-[#ebf3fc] text-[#4a90d9] flex items-center justify-center mb-3"><FolderHeart size={18}/></div>
          <div className="font-semibold text-[#1a2b4a]">Care Passport</div>
          <div className="text-xs text-[#475569] mt-1">Notes, meds, referrals</div>
        </Link>
        <Link to="/patient/messages" className="cm-card group" data-testid="p-quick-msg">
          <div className="w-10 h-10 rounded-lg bg-[#ebf3fc] text-[#4a90d9] flex items-center justify-center mb-3"><MessagesSquare size={18}/></div>
          <div className="font-semibold text-[#1a2b4a]">Messages</div>
          <div className="text-xs text-[#475569] mt-1">Talk to your doctors</div>
        </Link>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="cm-card lg:col-span-2 border-[#3daa6e]/30 bg-gradient-to-br from-white to-[#eaf7f0]" data-testid="p-referral-card">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-lg bg-[#3daa6e] text-white flex items-center justify-center flex-shrink-0"><Gift size={20}/></div>
            <div className="flex-1 min-w-0">
              <div className="cm-eyebrow text-[#3daa6e]">Refer a friend</div>
              <h3 className="font-semibold text-[#1a2b4a]">Share CAREMatch — get £10, give £10</h3>
              <p className="text-sm text-[#475569] mt-1">Send your link. When a friend books their first consultation, you both get £10 credit.</p>
              <div className="mt-3 flex flex-col sm:flex-row items-stretch gap-2">
                <div className="flex-1 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm font-mono text-[#1a2b4a] truncate" data-testid="p-referral-link">{shareUrl || "…"}</div>
                <button onClick={copy} className="cm-btn-primary" data-testid="p-referral-copy">
                  {copied ? <><Check size={14}/> Copied</> : <><Copy size={14}/> Copy link</>}
                </button>
              </div>
              {referral?.referred_count > 0 && (
                <div className="mt-2 text-xs text-[#3daa6e] font-semibold">{referral.referred_count} friends joined via your link</div>
              )}
            </div>
          </div>
        </div>
        <div className="cm-card" data-testid="p-credit-card">
          <div className="cm-eyebrow mb-1">Account credit</div>
          <div className="text-3xl font-bold text-[#1a2b4a]"><Money amount={credits?.balance ?? 0}/></div>
          <p className="text-xs text-[#475569] mt-2">Automatically applied at checkout when you book.</p>
        </div>
      </div>

      <div className="cm-card" data-testid="p-leaderboard-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#fffbeb] text-[#d97706] flex items-center justify-center"><Trophy size={16}/></div>
            <h3 className="font-semibold text-[#1a2b4a]">Top referrers this month</h3>
          </div>
          <span className="text-xs text-[#94a3b8]">Every friend booked = £10 you</span>
        </div>
        {leaderboard.length === 0 ? (
          <div className="text-sm text-[#475569]">Be the first — share your link above and take the top spot.</div>
        ) : (
          <ol className="divide-y divide-[#e2e8f0]">
            {leaderboard.map((r, i) => (
              <li key={r.user_id} className={`py-3 flex items-center gap-3 ${r.is_you ? "bg-[#ebf3fc] -mx-6 px-6" : ""}`} data-testid={`leaderboard-row-${i}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${i===0 ? "bg-[#fffbeb] text-[#d97706]" : i===1 ? "bg-[#f5f7fa] text-[#475569]" : i===2 ? "bg-[#fdf2e9] text-[#c2410c]" : "bg-[#f5f7fa] text-[#94a3b8]"}`}>{i+1}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#1a2b4a] truncate">{r.name} {r.is_you && <span className="text-xs text-[#4a90d9] font-normal">· that's you</span>}</div>
                  <div className="text-xs text-[#475569]">{r.count} {r.count === 1 ? "friend" : "friends"} joined · <Money amount={r.earned}/> earned</div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>

      <div className="cm-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-[#1a2b4a]">Upcoming appointments</h3>
          <Link to="/patient/appointments" className="text-sm font-semibold text-[#4a90d9]" data-testid="p-view-all-appts">View all <ArrowRight size={12} className="inline"/></Link>
        </div>
        {upcoming.length === 0 && <div className="text-sm text-[#475569]">No upcoming appointments. <Link to="/patient/intake" className="text-[#4a90d9] font-semibold">Start with AI intake</Link>.</div>}
        <div className="space-y-2">
          {upcoming.map(a => (
            <div key={a.id} className="flex items-center justify-between p-3 rounded-lg border border-[#e2e8f0]" data-testid={`p-appt-${a.id}`}>
              <div>
                <div className="text-sm font-semibold text-[#1a2b4a]">{a.doctor_name}</div>
                <div className="text-xs text-[#475569]">{(a.slot_datetime||"").replace("T"," ").slice(0,16)} · <span className="capitalize">{a.consultation_type}</span></div>
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge status={a.status}/>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
